<script setup lang="ts">
import ProgressFrame from '../layout/ProgressFrame.vue';
import type { WarrantyContact } from '../../models/Project';
import { reactive, onMounted, ref, computed } from 'vue';
import { useProjectStore } from '../../stores/projectStore';
import { isEmptyObject } from '@/utils/object';
import { validateIsEmail } from '@/utils/validators';
import type { Rule, RuleObject } from 'ant-design-vue/es/form';
import { useI18n } from 'vue-i18n';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import BaseForm from '@/shared/components/BaseForm.vue';
import ChevronRightSVG from '@shared/assets/icon/chevron-right.svg';
import { message } from 'ant-design-vue';
import { useRouter } from 'vue-router';
import HorizontalContentRow from '../../components/HorizontalContentRow.vue';
import ProjectNotFound from '../../components/ProjectNotFound.vue';
import { projectHeaderDisplay } from '../../utils/project-and-site';

const props = defineProps<{
  projectId: number;
}>();

const { t } = useI18n();
const router = useRouter();

const projectStore = useProjectStore();
const project = projectStore.getProjectById(props.projectId);
const projectHeader = computed(() => {
  return projectHeaderDisplay(props.projectId, project?.name ?? null);
});

const formRef = ref();

const formData = reactive<WarrantyContact>({
  company: '',
  firstName: '',
  lastName: '',
  contactEmail: '',
  contactPhone: '',
});

onMounted(() => {
  const warrantyContact = projectStore.warrantyContact;
  if (warrantyContact && !isEmptyObject(warrantyContact)) {
    formData.company = warrantyContact.company;
    formData.firstName = warrantyContact.firstName;
    formData.lastName = warrantyContact.lastName;
    formData.contactEmail = warrantyContact.contactEmail;
    formData.contactPhone = warrantyContact.contactPhone;
  }
});

const validateEmailAddress = (_: Rule, value: string) => {
  if (!value || value.trim() === '') {
    return Promise.reject('Contact email is required');
  }
  if (!validateIsEmail(value)) {
    return Promise.reject('Email is not valid.');
  }
  return Promise.resolve();
};

const validationRules: Record<string, RuleObject[]> = {
  company: [
    {
      required: true,
      message: 'Company is required',
      trigger: ['blur', 'change'],
    },
  ],
  firstName: [
    {
      required: true,
      message: 'First name is required',
      trigger: ['blur', 'change'],
    },
  ],
  lastName: [
    {
      required: true,
      message: 'Last name is required',
      trigger: ['blur', 'change'],
    },
  ],
  contactEmail: [
    {
      validator: validateEmailAddress,
      trigger: ['blur', 'change'],
    },
  ],
  contactPhone: [],
};

const handleSubmit = async () => {
  try {
    await formRef.value?.validate();
    await projectStore.saveWarrantyContact(props.projectId, formData as WarrantyContact);
    router.push({ name: 'ProjectSelectSite', params: { projectId: props.projectId } });
  } catch (error: unknown) {
    console.error('Validation failed:', error);
    message.error((error as Error).message);
  }
};
</script>

<template>
  <ProgressFrame pageClass="subsection-page" titleTight>
    <template #title>Warranty contact</template>
    <template #subTitle>
      <div class="project-name">
        <span class="project-name__title">{{ projectHeader?.heading }}</span>
        <span v-if="projectHeader?.subheading" class="project-name__subtitle">
          {{ projectHeader?.subheading }}
        </span>
      </div>
    </template>

    <template v-if="!project">
      <div class="content content--project-not-found">
        <ProjectNotFound />
      </div>
    </template>
    <template v-else>
      <div class="content content--form">
        <div class="page-description">
          <p>{{ t('commissioning.projectWarrantyContact.pageDescription') }}</p>
        </div>

        <HorizontalContentRow firstRow lastRow>
          <div class="form-container">
            <BaseForm
              ref="formRef"
              :model="formData"
              :rules="validationRules"
              @finish="handleSubmit"
              id="warranty-contact-form"
            >
              <a-form-item name="company" :rules="validationRules.company">
                <template #label>
                  <span>Company</span>
                </template>
                <a-input
                  v-model:value="formData.company"
                  placeholder="Please input company"
                  size="large"
                  data-tn="company-input"
                />
              </a-form-item>
              <a-form-item name="firstName" :rules="validationRules.firstName">
                <template #label>
                  <span>First name</span>
                </template>
                <a-input
                  v-model:value="formData.firstName"
                  placeholder="Please input first name"
                  size="large"
                  data-tn="first-name-input"
                />
              </a-form-item>
              <a-form-item name="lastName" :rules="validationRules.lastName">
                <template #label>
                  <span>Last name</span>
                </template>
                <a-input
                  v-model:value="formData.lastName"
                  placeholder="Please input last name"
                  size="large"
                  data-tn="last-name-input"
                />
              </a-form-item>
              <a-form-item name="contactEmail" :rules="validationRules.contactEmail">
                <template #label>
                  <span>Contact email</span>
                </template>
                <a-input
                  v-model:value="formData.contactEmail"
                  placeholder="Please input contact email"
                  size="large"
                  data-tn="contact-email-input"
                />
              </a-form-item>
              <a-form-item name="contactPhone" :rules="validationRules.contactPhone">
                <template #label>
                  <span>Contact phone</span>
                </template>
                <a-input
                  v-model:value="formData.contactPhone"
                  placeholder="Please input contact phone"
                  size="large"
                  data-tn="contact-phone-input"
                />
              </a-form-item>
            </BaseForm>
          </div>
        </HorizontalContentRow>
        <div class="end-of-page">
          <ButtonPrimary
            html-type="submit"
            form="warranty-contact-form"
            class="submit-button"
            block
            data-tn="warranty-contact-submit"
          >
            Next
            <template #iconRight>
              <ChevronRightSVG class="chevron-right-icon" />
            </template>
          </ButtonPrimary>
        </div>
      </div>
    </template>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.project-name {
  &__title {
    font-weight: @font-weight-medium;
  }

  &__subtitle {
    display: block;
  }
}

.content.content--project-not-found {
  margin-top: @margin-top-end-of-page-section;
}

.content.content--form {
  padding: @content-side-margin 0;
  .content-row {
    width: 100%;
  }
}

.page-description {
  margin: @content-side-margin;
  font-weight: @font-weight-medium;
  text-align: center;
}

.form-container {
  width: 100%;
}

.ant-form-item {
  margin-bottom: 1rem;
}

.end-of-page {
  padding: 0 @content-side-margin;
  margin-top: @margin-top-end-of-page-section;
  width: 100%;
}
</style>
